<template>
  <FormGroup>
    <FormField
      v-for="(value, key) in amenities"
      :key="key"
      :label="value"
      direction="row"
      :error="form.fieldErrors(key)"
    >
      <CheckmarkCrossmark v-model="form.fields[key]" />
    </FormField>
  </FormGroup>
</template>

<script setup lang="ts">
import { CheckmarkCrossmark, FormField, FormGroup, useForm } from '@tallo/design-system';
import { amenityNamesMap, Amenity } from '@tallo/property';

import { useDetailsHolder } from '../../composables/details-holder';
import { SaveAmenitiesDto } from '../../dtos/save-amenities.dto.interface';
import { buildingAmenityKeys } from '../../utils/building-amenity-keys';

const holder = useDetailsHolder();
const form = useForm<SaveAmenitiesDto>({
  ...getItems(),
});

// A building offers only the building-level amenities, the ones it pushes to every unit
const amenities = Object.fromEntries(
  Object.entries(amenityNamesMap)
    .filter(([key]) => holder.kind !== 'building' || buildingAmenityKeys.includes(key as Amenity))
    .sort((a, b) => a[1].localeCompare(b[1])),
) as { [key in Amenity]: string };

function getItems(): { [key in Amenity]: boolean | null } {
  const amenities = new Map<Amenity, boolean | null>();

  Object.keys(amenityNamesMap).forEach((key) => {
    amenities.set(key as Amenity, holder.details?.amenities?.[key as Amenity] ?? null);
  });

  return Object.fromEntries(amenities) as { [key in Amenity]: boolean | null };
}

async function save() {
  // Only the keys on screen are sent, so a building never writes a unit's own keys
  return await form.submit((fields) =>
    holder.saveAmenities(
      Object.fromEntries(Object.keys(amenities).map((key) => [key, fields[key as Amenity]])) as SaveAmenitiesDto,
    ),
  );
}

defineExpose({ save, form });
</script>
